import {
  leftReceptacle,
  leftSpout,
  rightReceptacle,
  rightSpout,
  type LevelConfig,
} from './portHelpers';

export const LEVEL_7: LevelConfig = {
  step: 7,
  title: 'STEP SEVEN',
  instructions: 'Oxygen and CO₂ both need PRESSURE to get where they\'re going. Place the heart and wire both circulation lanes.',
  // Wall ports route across the heart diagonally:
  //   CO₂ lane: bot-left spout (in) → top-of-heart → bot-of-heart → bot-right receptacle.
  //   O₂  lane: top-right spout (in) → top-of-heart → bot-of-heart → top-left receptacle.
  // Spouts hide their molecule icon — student discovers what's flowing by
  // wiring them up. Receptacles keep their icons (they tell the student what
  // mix is expected there).
  //
  // Both discovery spouts gate on the matching receptacle (wall-port
  // `dependsOn`, same pattern as Level 6's mystery O₂ passthrough). The
  // narrative is "body finishes its half of the loop, then releases the
  // returned blood." Heart.A-out is the unconditional kickstart that gets
  // the cascade rolling: it pumps CO₂ to s7-co2-out (the receptacle),
  // which opens s7-o2-in (the spout), which flows through the heart to
  // s7-o2-out, which opens s7-co2-in, closing the loop.
  ports: [
    // Bottom-left: CO₂ blood spout. Visual is blank — student figures it out.
    // Fires only after O₂ has arrived at the top-left receptacle (body
    // received O₂ → body now sends deoxygenated blood back).
    {
      ...leftSpout('s7-co2-in', 11, ['co2']),
      hideContents: true,
      dependsOn: ['s7-o2-out'],
    },
    // Top-left: O₂ blood receptacle (where oxygenated blood ends up — to body).
    leftReceptacle('s7-o2-out', 4, ['oxygen']),
    // Top-right: O₂ blood spout. Visual blank — discovery.
    // Fires only after CO₂ has arrived at the bot-right receptacle (lungs
    // received CO₂ → lungs now return oxygenated blood).
    {
      ...rightSpout('s7-o2-in', 4, ['oxygen']),
      hideContents: true,
      dependsOn: ['s7-co2-out'],
    },
    // Bottom-right: CO₂ blood receptacle (deoxygenated blood — to lungs).
    rightReceptacle('s7-co2-out', 11, ['co2']),
  ],
  organPool: [
    {
      id: 'heart',
      art: 'heart',
      cols: 4,
      // 4 rows so the body texture reaches down to the row where the bottom
      // output cones protrude (the visual "bottom chamber" fills cells (1,3)
      // through (4,4) of the heart's 4x4 internal grid).
      rows: 4,
      // Heart pulses when both lanes are pumping (both outputs flowing).
      activatesWhen: ['s7-heart-A-out', 's7-heart-B-out'],
      ports: [
        // TOP-LEFT INPUT (A-in): CO₂-blood arriving from the body.
        {
          id: 's7-heart-A-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 0, dRow: -1 },
          carries: ['co2'],
        },
        // BOTTOM-LEFT OUTPUT (A-out): UNCONDITIONAL. The heart is "always
        // pumping" deoxygenated blood toward the lungs — emits CO₂ as soon
        // as the heart is placed and fluids run, regardless of whether A-in
        // is connected. Spurts CO₂ at the cone tip when no pipe is attached.
        {
          id: 's7-heart-A-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 0, dRow: 3 },
          carries: ['co2'],
        },
        // TOP-RIGHT INPUT (B-in): O₂-blood arriving from the lungs.
        {
          id: 's7-heart-B-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 3, dRow: -1 },
          carries: ['oxygen'],
        },
        // BOTTOM-RIGHT OUTPUT (B-out): passthrough — emits O₂ only when B-in
        // is receiving O₂.
        {
          id: 's7-heart-B-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 3, dRow: 3 },
          carries: ['oxygen'],
          dependsOn: ['s7-heart-B-in'],
        },
      ],
    },
  ],
};
